// Exportación del cuadro de mando (change 003). Pura: transforma las filas que el
// cuadro ya tiene cargadas y filtradas (D3) —no consulta nada ni filtra— en filas
// para el Excel y en el nombre del fichero. Cero IO, cero DOM.

import { ETIQUETA_MOTIVO, derivadoA, etiquetaMotivo, textoEstado } from '../llamadas/presentacion';
import type { LlamadaFeedRow } from '../../types/llamadas';
import { SIN_CLIENTE, etiquetaCliente, type FiltrosCuadro } from './filtros';

export const COLUMNAS_EXPORTACION = [
  'Fecha',
  'Cliente',
  'Motivo',
  'Estado',
  'Derivado a',
  'Duración (s)',
] as const;

// Una llamada sin cliente identificado sale así y NUNCA con su teléfono (D7): el
// teléfono de un particular es un dato personal, y un fichero descargado sale de
// la superficie que gobernamos. La pantalla sí lo enseña.
export const SIN_IDENTIFICAR = 'Sin identificar';

const SIN_MOTIVO = ETIQUETA_MOTIVO.sin_motivo ?? 'Sin clasificar';

export interface FilaExportable {
  fecha: string | null; // 'YYYY-MM-DDTHH:MM:SS' en hora de pared de Madrid
  cliente: string;
  motivo: string;
  estado: string;
  derivadoA: string | null;
  duracionS: number | null;
}

// Celdas tipadas: el escritor del .xlsx no sabe nada de llamadas.
export type Celda =
  | { t: 'texto'; v: string }
  | { t: 'numero'; v: number }
  | { t: 'fecha'; v: string }
  | { t: 'vacia' };

export interface Hoja {
  cabecera: string[];
  filas: Celda[][];
}

// Un .xlsx no tiene zona horaria: una fecha es un número de serie que Excel
// enseña tal cual. Si se guardase el instante UTC, en Madrid saldría una o dos
// horas antes de lo que dice el cuadro. Se guarda la hora de pared de Madrid.
// `hourCycle: 'h23'` evita el «24:00» que algunos motores dan a medianoche.
//
// Con segundos, aunque la hoja enseñe solo la hora y el minuto: así dos llamadas
// del mismo minuto se ordenan bien. Límite del formato: en la hora que se repite
// al atrasar el reloj en octubre, dos instantes distintos tienen la misma hora de
// pared. El orden de las filas sigue siendo el real, porque es el del feed.
const PARTES_MADRID = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Madrid',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

// Una fecha ilegible cuenta como «sin fecha», igual que `horaMadrid`: un valor
// raro no puede tumbar la exportación entera.
function fechaMadrid(iso: string | null): string | null {
  if (!iso) return null;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  const p = Object.fromEntries(PARTES_MADRID.formatToParts(t).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}`;
}

// LÍMITE del cinturón, el mismo que el de la pantalla y escrito entero en
// `etiquetaMotivo`: por veracidad se cubren la ausencia del motivo y la cadena vacía,
// que salen las dos como «Sin clasificar». Un motivo visualmente vacío (`'  '`,
// `'\u200B'`) llega hasta la celda y deja una que parece en blanco en el fichero que se
// manda por correo; uno con invisibles delante da dos filas que se leen igual y que
// tampoco se agrupan en una tabla dinámica. Lo cierra la frontera del dato al escribir
// —que viaja en el cambio `registro-llamadas` y no está en este árbol—, no esto.
export function filasExportables(llamadas: LlamadaFeedRow[]): FilaExportable[] {
  return llamadas.map((l) => ({
    fecha: fechaMadrid(l.inicio),
    cliente: l.id_cliente === null ? SIN_IDENTIFICAR : etiquetaCliente(l),
    motivo: l.motivo ? etiquetaMotivo(l.motivo) : SIN_MOTIVO,
    estado: textoEstado(l),
    derivadoA: derivadoA(l),
    duracionS: l.duracion_s,
  }));
}

const texto = (v: string): Celda => ({ t: 'texto', v });

function opcional<T>(v: T | null, celda: (v: T) => Celda): Celda {
  return v === null ? { t: 'vacia' } : celda(v);
}

// Lo que no hay va VACÍO, no como «—»: en una hoja de cálculo un vacío se puede
// filtrar y contar, y un guion es un texto más.
export function hojaExportacion(filas: FilaExportable[]): Hoja {
  return {
    cabecera: [...COLUMNAS_EXPORTACION],
    filas: filas.map((f) => [
      opcional(f.fecha, (v) => ({ t: 'fecha', v })),
      texto(f.cliente),
      texto(f.motivo),
      texto(f.estado),
      opcional(f.derivadoA, texto),
      opcional(f.duracionS, (v) => ({ t: 'numero', v })),
    ]),
  };
}

// --- Nombre del fichero (T3) ---

const PREFIJO = 'aramatica-llamadas';
const LARGO_MAXIMO = 120;
// Ocho caracteres de un uuid: que dos clientes con el mismo nombre coincidan
// también en ellos es una probabilidad entre cuatro mil millones.
const LARGO_ID = 8;

// Solo [a-z0-9-]: sin acentos, sin espacios y sin nada que un sistema de ficheros
// rechace.
function slug(valor: string): string {
  return valor
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export interface ContextoNombre {
  diaDesde: string; // YYYY-MM-DD
  diaHasta: string; // YYYY-MM-DD
  filtros: FiltrosCuadro;
  razonSocialCliente?: string | null;
}

// Nombre BASE, sin extensión: el mismo para el Excel y para el PDF. Lleva el rango
// y cada filtro activo en un orden fijo, para que dos descargas distintas no se
// llamen igual en la carpeta de nadie.
//
// Detrás de la razón social va un trozo de su id: dos clientes pueden llamarse
// igual (la réplica lo permite) o empezar igual, y con los cuatro filtros puestos
// del nombre caben pocas letras.
//
// No pasa de 120 caracteres. Si no cabe, se recorta el texto libre más largo
// (rama, motivo o razón social), letra a letra y sin dejarlo vacío. Lo que no es
// texto libre —el rango, el desenlace, «sin-identificar» y el id— no se toca.
interface Trozo {
  etiqueta: string;
  texto: string;
  recortable: boolean;
  sufijo?: string;
}

export function nombreExportacion({
  diaDesde,
  diaHasta,
  filtros,
  razonSocialCliente,
}: ContextoNombre): string {
  // Un texto libre nunca acaba en guion, ni al cortarlo aquí ni al recortarlo abajo:
  // si acabara, el guion no saldría en el nombre pero contaría al elegir qué
  // recortar, y el bucle gastaría vueltas sin acortar nada (vuelta 3 del gate).
  const sinGuionFinal = (texto: string) => texto.replace(/-+$/, '');
  // El tope previo acota el bucle: una razón social de diez mil letras no puede
  // costar diez mil vueltas.
  const libre = (valor: string) => sinGuionFinal(slug(valor).slice(0, LARGO_MAXIMO));
  const trozos: Trozo[] = [];
  if (filtros.rama) trozos.push({ etiqueta: 'rama', texto: libre(filtros.rama), recortable: true });
  if (filtros.motivo) {
    trozos.push({ etiqueta: 'motivo', texto: libre(filtros.motivo), recortable: true });
  }
  if (filtros.desenlace) {
    trozos.push({ etiqueta: 'desenlace', texto: slug(filtros.desenlace), recortable: false });
  }
  if (filtros.cliente === SIN_CLIENTE) {
    trozos.push({ etiqueta: 'cliente', texto: 'sin-identificar', recortable: false });
  } else if (filtros.cliente !== null) {
    const id = filtros.cliente
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .slice(0, LARGO_ID);
    trozos.push({
      etiqueta: 'cliente',
      texto: libre(razonSocialCliente ?? '') || 'sin-nombre',
      recortable: true,
      sufijo: id ? `-${id}` : '',
    });
  }

  // Un filtro cuyo valor no deja ninguna letra válida (otro alfabeto, solo símbolos)
  // sale solo con su etiqueta, sin un guion colgando.
  const componer = () =>
    [
      PREFIJO,
      diaDesde,
      diaHasta,
      ...trozos.map((t) => `${t.etiqueta}${t.texto ? `-${t.texto}` : ''}${t.sufijo ?? ''}`),
    ].join('_');

  // Cada vuelta acorta el nombre al menos una letra, así que el bucle acaba: o cabe,
  // o ya no queda texto libre que recortar.
  while (componer().length > LARGO_MAXIMO) {
    const mayor = trozos
      .filter((t) => t.recortable && t.texto.length > 1)
      .reduce<Trozo | null>((a, t) => (a && a.texto.length >= t.texto.length ? a : t), null);
    if (!mayor) break;
    mayor.texto = sinGuionFinal(mayor.texto.slice(0, -1));
  }
  return componer();
}
